import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import { db } from "./register/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";

import { PenBox } from "lucide-react";
import { Trash } from "lucide-react";

function BlogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [newComment, setNewComment] = useState("");

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postRef = doc(db, "posts", id);
        const snapshot = await getDoc(postRef);
        if (snapshot.exists()) {
          setPost(snapshot.data());
        }
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  useEffect(() => {
    const q = query(
      collection(db, "posts", id, "comments"),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedComments = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(fetchedComments);
    });

    return unsubscribe;
  }, [id]);

  const handleAddComment = async () => {
    if (!user || !newComment.trim()) return;

    await addDoc(collection(db, "posts", id, "comments"), {
      text: newComment,
      author: user.displayName || user.email || "Anonymous",
      createdAt: new Date(),
    });

    setNewComment("");
  };

  const handleDeletePost = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this post?"
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "posts", id));
      alert("Post deleted successfully!");
      navigate("/");
    } catch (error) {
      console.error("Error deleting post:", error);
      alert("Failed to delete the post.");
    }
  };

  const handleEditPost = () => {
    navigate(`/blog-editor/${id}`, {
      state: { post },
    });
  };

  const getInitials = (name = "") => {
    if (!name) return "AU";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-opacity-75 mb-4"></div>
          <p className="text-lg font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );

  if (!post)
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Post Not Found
          </h1>
          <p className="text-gray-600">
            Sorry, we couldn’t find the post you’re looking for.
          </p>
          <a
            href="/"
            className="mt-6 inline-block px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition duration-300"
          >
            Go Back to Home
          </a>
        </div>
      </div>
    );

  return (
    <div className="mx-auto px-4 py-12 md:px-8 py-28 bg-white">
      <div className="flex items-center justify-between mb-6">
        {/* Left: Avatar + Title */}
        <div className="flex items-center gap-4">
          <div className="group relative flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold">
            {getInitials(post.author)}
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm bg-blue bg-opacity-0 text-black rounded-full p-20">
              {user
                ? user.displayName || user.email
                : post.author || "Unknown Author"}
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-900">
            {post.title || "Untitled"}
          </h1>
        </div>

        {/* Right: Icon Buttons */}
        {user?.uid === post?.uid && (
          <div className="flex gap-3">
            <button
              title="Edit"
              className="text-black-500 hover:text-yellow-600 transition text-2xl"
              onClick={handleEditPost}
            >
              <PenBox className="w-6 h-6" />
            </button>
            <button
              title="Delete"
              className="text-red-600 hover:text-red-700 transition text-2xl"
              onClick={handleDeletePost}
            >
              <Trash className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>

      {/* Post Content */}
      <div
        className="prose max-w-full break-words mb-8 px-4 md:px-20"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />

      {/* Comments Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Comments</h2>

        {user ? (
          <div className="mb-6">
            <textarea
              className="w-full border rounded-lg p-4 mb-3 text-sm placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <button
              onClick={handleAddComment}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
            >
              Submit
            </button>
          </div>
        ) : (
          <p className="text-gray-500 mb-4">Log in to leave a comment.</p>
        )}

        {/* Render comments */}
        <div className="space-y-6">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="bg-gray-100 p-4 rounded-lg flex gap-4 items-start shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                {getInitials(comment.author)}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">
                  {comment.author}
                </p>
                <p className="text-sm text-gray-600">{comment.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BlogDetails;
